import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BcmCanvas, type BcmCanvasHandle } from '../bcm/BcmCanvas';
import { BcmAddNodeModal } from '../bcm/BcmAddNodeModal';
import ToolbarBcm from './ToolbarBcm';
import { useBcmBoard } from '../../hooks/useBcmBoard';
import type { MetaRegistryEntry } from '../../types/systems';
import { useTheme } from '../../hooks/useTheme';
import { listRelations } from '../../api/metaRegistriesApi';
import type { BcmNode } from '../../api/bcmApi';

interface Props {
  mapEntryId?: string;
  mapName?: string;
  mapAttributes?: Record<string, unknown>;
  maps?: MetaRegistryEntry[];
  selectedMapId?: string;
  onSelectMap?: (mapEntryId: string) => void;
  onCreateMap?: () => void;
  creatingMap?: boolean;
  diagramId?: string | null;
  projectId?: string;
}

interface AddPending {
  parentBcEntryId: string | null;
  x: number;
  y: number;
}

interface ContextMenuState {
  node: BcmNode;
  x: number;
  y: number;
  systems: Array<{ id: string; name: string; code?: string }>;
  loading: boolean;
}

const LoadedBoardBcm: React.FC<{
  projectId: string;
  mapEntryId: string;
  mapName?: string;
  mapAttributes: Record<string, unknown>;
  maps: MetaRegistryEntry[];
  selectedMapId: string;
  onSelectMap?: (mapEntryId: string) => void;
  onCreateMap?: () => void;
  creatingMap: boolean;
}> = ({
  projectId,
  mapEntryId,
  mapName,
  mapAttributes,
  maps,
  selectedMapId,
  onSelectMap,
  onCreateMap,
  creatingMap,
}) => {
  const [addPending, setAddPending] = useState<AddPending | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const canvasRef = useRef<BcmCanvasHandle>(null);

  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    document.addEventListener('mousedown', handler);
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setContextMenu(null); };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [contextMenu]);
  const board = useBcmBoard(projectId, mapEntryId, mapAttributes);
  const { theme } = useTheme();
  const existingBcIds = useMemo(() => new Set(board.allNodes.map((node) => node.bc_entry_id)), [board.allNodes]);
  const breadcrumb = board.navigation.navState.breadcrumb;
  const isAutoMap = mapAttributes.auto_populated === true || mapAttributes.system_managed === true;

  const handleAddConfirm = async (entry: MetaRegistryEntry) => {
    if (!addPending) return;
    await board.addNode(entry.id, addPending.parentBcEntryId, addPending.x, addPending.y);
    setAddPending(null);
  };

  const handleNodeContextMenu = async (
    node: BcmNode,
    clientX: number,
    clientY: number,
  ) => {
    const targetEntryId = node.bc_entry_id;
    setContextMenu({ node, x: clientX, y: clientY, systems: [], loading: true });
    try {
      const relations = await listRelations(projectId, { to: targetEntryId });
      const systems = relations
        .filter((r) => r.relation_type?.slug === 'implements')
        .map((r) => r.from_entry)
        .filter(Boolean) as Array<{ id: string; name: string; code?: string }>;
      setContextMenu(prev =>
        prev?.node.bc_entry_id === targetEntryId ? { ...prev, systems, loading: false } : prev
      );
    } catch {
      setContextMenu(prev =>
        prev?.node.bc_entry_id === targetEntryId ? { ...prev, systems: [], loading: false } : prev
      );
    }
  };

  const currentParentForAdd = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].id : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: theme.background }}>
      <ToolbarBcm
        onSync={() => void board.sync()}
        onAdd={isAutoMap ? undefined : () => setAddPending({ parentBcEntryId: currentParentForAdd, x: 80, y: 80 })}
        onExportPng={() => void canvasRef.current?.exportPng(`${breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].name : 'bcm-map'}.png`)}
        title={breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].name : mapName ?? 'BCM'}
        modeLabel={isAutoMap ? 'Default auto-map' : 'Custom map'}
        maps={maps}
        selectedMapId={selectedMapId}
        onSelectMap={onSelectMap}
        onCreateMap={onCreateMap}
        creatingMap={creatingMap}
      />

      {breadcrumb.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            background: theme.toolbar.background,
            borderBottom: `1px solid ${theme.toolbar.border}`,
            color: theme.toolbar.text,
            fontSize: 13,
          }}
        >
          <button
            type="button"
            onClick={board.navigation.goBack}
            style={{
              border: `1px solid ${theme.toolbar.border}`,
              background: theme.toolbar.buttonBackground,
              color: theme.toolbar.text,
              borderRadius: 6,
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            ← Уровень выше
          </button>
          <span style={{ color: '#2563eb', cursor: 'pointer' }} onClick={board.navigation.reset}>
            Корень
          </span>
          {breadcrumb.map((item, index) => (
            <React.Fragment key={item.id}>
              <span style={{ opacity: 0.5 }}>/</span>
              <span
                style={{
                  color: index < breadcrumb.length - 1 ? '#2563eb' : theme.toolbar.text,
                  cursor: index < breadcrumb.length - 1 ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (index < breadcrumb.length - 1) board.navigation.navigateTo(item);
                }}
              >
                {item.name}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {board.ghostCount > 0 && (
        <div
          style={{
            padding: '8px 16px',
            background: '#7c2d12',
            color: '#fed7aa',
            fontSize: 13,
            borderBottom: '1px solid #9a3412',
          }}
        >
          Удалено устаревших узлов: {board.ghostCount}
        </div>
      )}

      <div style={{ flex: 1, position: 'relative' }}>
        {board.loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: theme.toolbar.text,
            }}
          >
            Загрузка карты…
          </div>
        ) : board.error ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#f87171',
            }}
          >
            Ошибка: {board.error}
          </div>
        ) : board.allNodes.length === 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: theme.toolbar.text,
              opacity: 0.75,
              textAlign: 'center',
              padding: 24,
            }}
          >
            На этой BCM-карте пока нет бизнес-способностей.
          </div>
        ) : (
          <BcmCanvas
            ref={canvasRef}
            parentNodes={board.parentNodes}
            childNodes={board.childNodes}
            displayRules={board.displayRules}
            onDrillDown={board.navigation.drillDown}
            onUpdatePosition={board.updatePosition}
            onAddChildRequest={(parentBcEntryId, x, y) => {
              if (!isAutoMap) setAddPending({ parentBcEntryId, x, y });
            }}
            canAddChildren={!isAutoMap}
            onContextMenu={handleNodeContextMenu}
          />
        )}
      </div>

      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9000,
            background: theme.toolbar.background,
            border: `1px solid ${theme.toolbar.border}`,
            borderRadius: 8,
            padding: '12px 16px',
            minWidth: 220,
            maxWidth: 320,
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div style={{
            fontWeight: 600, fontSize: 13, color: theme.toolbar.text, marginBottom: 8,
          }}>
            {contextMenu.node.entry?.name ?? 'Бизнес-способность'}
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, color: theme.toolbar.text, marginBottom: 8 }}>
            Реализующие системы:
          </div>
          {contextMenu.loading ? (
            <div style={{ fontSize: 12, opacity: 0.6, color: theme.toolbar.text }}>Загрузка...</div>
          ) : contextMenu.systems.length === 0 ? (
            <div style={{ fontSize: 12, opacity: 0.6, color: theme.toolbar.text }}>Нет привязанных систем</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {contextMenu.systems.map((sys) => (
                <div
                  key={sys.id}
                  style={{
                    fontSize: 13,
                    color: theme.toolbar.text,
                    padding: '4px 0',
                    borderBottom: `1px solid ${theme.toolbar.border}`,
                  }}
                >
                  {sys.name}
                  {sys.code && (
                    <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 6 }}>({sys.code})</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => setContextMenu(null)}
            style={{
              marginTop: 10,
              fontSize: 12,
              cursor: 'pointer',
              background: 'transparent',
              border: 'none',
              color: theme.toolbar.text,
              opacity: 0.6,
              padding: 0,
            }}
          >
            Закрыть
          </button>
        </div>
      )}

      <BcmAddNodeModal
        open={addPending !== null}
        mapEntryId={mapEntryId}
        existingBcIds={existingBcIds}
        parentBcEntryId={addPending?.parentBcEntryId ?? null}
        onConfirm={handleAddConfirm}
        onClose={() => setAddPending(null)}
      />
    </div>
  );
};

const BoardBcm: React.FC<Props> = ({
  projectId,
  mapEntryId,
  mapName,
  mapAttributes = {},
  maps = [],
  selectedMapId,
  onSelectMap,
  onCreateMap,
  creatingMap = false,
}) => {
  const { theme } = useTheme();
  if (!projectId || !mapEntryId) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: theme.toolbar.text,
          background: theme.background,
          fontSize: 14,
        }}
      >
        BCM теперь открывается как registry-driven board, а не как diagram editor.
      </div>
    );
  }

  return (
    <LoadedBoardBcm
      projectId={projectId}
      mapEntryId={mapEntryId}
      mapName={mapName}
      mapAttributes={mapAttributes}
      maps={maps}
      selectedMapId={selectedMapId ?? mapEntryId}
      onSelectMap={onSelectMap}
      onCreateMap={onCreateMap}
      creatingMap={creatingMap}
    />
  );
};

export default BoardBcm;
